import React from 'react';

export interface ProductionScheduleItem {
  id: string;
  stt: number;
  stepId?: string;
  stepName: string;
  processGroup: string;
  poId: string;
  poCode: string;
  poReference?: string;
  customerName: string;
  productCode?: string;
  productName: string;
  tenSp: string;
  slCap: number;
  slBu: number;
  slDuKien: number;
  slDat: number;
  slHu: number;
  haoHut: number;
  status: string;
  nguoiThucHien?: string;
  scheduledStartTime?: Date | string;
  scheduledEndTime?: Date | string;
  technicalRequirements?: string;
}

export interface ProductionScheduleReportPrintContentProps {
  reportData: {
    items: ProductionScheduleItem[];
    summary: {
      totalCap: number;
      totalBu: number;
      totalDuKien: number;
      totalDat: number;
      totalHu: number;
      totalHao: number;
      totalItems: number;
    };
    availableProcesses?: string[];
  };
  dateRange?: { from?: string; to?: string };
  selectedProcesses?: string[];
  config?: any;
}

export default function ProductionScheduleReportPrintContent({
  reportData,
  dateRange,
  selectedProcesses = [],
  config,
}: ProductionScheduleReportPrintContentProps) {
  const { summary, items = [] } = reportData || { summary: {}, items: [] };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatNumber = (val?: number | string) => {
    if (val === null || val === undefined || val === '') return '0';
    if (isNaN(Number(val))) return String(val);
    return Number(val).toLocaleString('vi-VN');
  };

  // Group items by process group / step name
  const groups: { [key: string]: ProductionScheduleItem[] } = {};
  items.forEach((item) => {
    const key = item.processGroup || item.stepName || 'Công đoạn khác';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });

  const groupKeys = Object.keys(groups).sort();

  return (
    <div
      id="print-area"
      className="kh-print-doc w-full max-w-[280mm] bg-white text-black p-[8mm] font-serif mx-auto text-[9.5pt] leading-tight print:p-0 print:m-0"
      style={{ fontFamily: '"Times New Roman", Times, serif' }}
    >
      {/* ========== HEADER COMPANY & ISO BADGE ========== */}
      <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3">
        <div className="flex-shrink-0 flex items-center gap-2 w-[110px]">
          {config?.logoUrl ? (
            <img
              src={config.logoUrl}
              alt="Logo"
              className="h-[50px] max-w-[110px] object-contain"
            />
          ) : (
            <div className="h-[50px] w-[95px] flex items-center justify-center border border-slate-400 bg-slate-50 text-slate-700 font-bold text-[11px]">
              APV PACK
            </div>
          )}
        </div>

        <div className="text-center flex-1 px-3">
          <div className="text-[19px] font-bold uppercase tracking-wide text-slate-900">
            BÁO CÁO KẾ HOẠCH SẢN XUẤT
          </div>
          <div className="text-[10pt] text-slate-700 mt-1 font-semibold">
            <span>Ngày sản xuất: </span>
            <strong>
              {dateRange?.from ? formatDate(dateRange.from) : 'Tất cả'}
              {dateRange?.to && dateRange.to !== dateRange.from ? ` — ${formatDate(dateRange.to)}` : ''}
            </strong>
            {selectedProcesses.length > 0 && (
              <span className="ml-2 text-indigo-800">
                | Công đoạn: <strong>{selectedProcesses.join(', ')}</strong>
              </span>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 flex flex-col items-end justify-center w-[110px] text-[8pt] text-slate-600 text-right">
          <div className="border border-slate-300 rounded px-1.5 py-1 bg-slate-50">
            <div className="font-semibold text-slate-800">BM-KHSX-01</div>
            <div>Ban hành: 01/2025</div>
          </div>
        </div>
      </div>

      {/* ========== SUMMARY CARDS (Screen only / Printable overview) ========== */}
      <div className="grid grid-cols-4 gap-2 mb-3 print:hidden text-white text-xs">
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">SL Cấp / Bù Hao</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalCap)} / {formatNumber(summary?.totalBu)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">SL Dự Kiến (Target)</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalDuKien)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">SL Đạt Chuẩn</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalDat)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded p-2.5 shadow-xs">
          <div className="text-[10.5px] opacity-90 font-medium">SL Hư / Phế Phẩm</div>
          <div className="text-base font-bold mt-0.5">
            {formatNumber(summary?.totalHu)}
          </div>
        </div>
      </div>

      {/* ========== DATA TABLE (9 COLUMNS A4 LANDSCAPE) ========== */}
      <table className="w-full border-collapse border border-black text-[9pt]">
        <thead>
          <tr className="bg-slate-800 text-white font-bold text-center text-[9pt] print:bg-slate-200 print:text-black">
            <th className="border border-black py-1.5 px-1 w-[8mm]">STT</th>
            <th className="border border-black py-1.5 px-2 text-left w-[125mm]">
              Tên Sản Phẩm / Mã LSX / Khách Hàng
            </th>
            <th className="border border-black py-1.5 px-1 text-right w-[20mm]">SL Cấp</th>
            <th className="border border-black py-1.5 px-1 text-right w-[18mm]">SL Bù</th>
            <th className="border border-black py-1.5 px-1 text-right w-[20mm]">Dự Kiến</th>
            <th className="border border-black py-1.5 px-1 text-right w-[20mm]">SL Đạt</th>
            <th className="border border-black py-1.5 px-1 text-right w-[18mm]">SL Hư</th>
            <th className="border border-black py-1.5 px-1 text-right w-[18mm]">Hao Hụt</th>
            <th className="border border-black py-1.5 px-1.5 text-left w-[33mm]">Người TH / Máy</th>
          </tr>
        </thead>
        <tbody>
          {groupKeys.length === 0 ? (
            <tr>
              <td colSpan={9} className="border border-black py-8 text-center text-slate-500 italic">
                Không có kế hoạch sản xuất nào trong khoảng thời gian này
              </td>
            </tr>
          ) : (
            groupKeys.map((groupName) => {
              const groupItems = groups[groupName];
              const subCap = groupItems.reduce((s, i) => s + (i.slCap || 0), 0);
              const subBu = groupItems.reduce((s, i) => s + (i.slBu || 0), 0);
              const subDk = groupItems.reduce((s, i) => s + (i.slDuKien || 0), 0);
              const subDat = groupItems.reduce((s, i) => s + (i.slDat || 0), 0);
              const subHu = groupItems.reduce((s, i) => s + (i.slHu || 0), 0);
              const subHao = groupItems.reduce((s, i) => s + (i.haoHut || 0), 0);

              return (
                <React.Fragment key={groupName}>
                  {/* Process Subheader Row with Subtotals */}
                  <tr className="bg-sky-100 font-bold text-slate-900 print:bg-slate-100 break-inside-avoid">
                    <td colSpan={2} className="border border-black py-1 px-2 text-left">
                      📋 {groupName} ({groupItems.length} công việc)
                    </td>
                    <td className="border border-black py-1 px-1 text-right">{formatNumber(subCap)}</td>
                    <td className="border border-black py-1 px-1 text-right">{formatNumber(subBu)}</td>
                    <td className="border border-black py-1 px-1 text-right">{formatNumber(subDk)}</td>
                    <td className="border border-black py-1 px-1 text-right">{formatNumber(subDat)}</td>
                    <td className="border border-black py-1 px-1 text-right text-rose-700">
                      {subHu > 0 ? formatNumber(subHu) : '0'}
                    </td>
                    <td className="border border-black py-1 px-1 text-right">
                      {formatNumber(subHao)}
                    </td>
                    <td className="border border-black py-1 px-1"></td>
                  </tr>

                  {/* Detail items */}
                  {groupItems.map((item, idx) => {
                    const isDone = item.slDat >= item.slDuKien && item.slDuKien > 0;
                    const completionPct = item.slDuKien > 0 ? Math.round((item.slDat / item.slDuKien) * 100) : 0;
                    const isExcessScrap = item.haoHut > item.slBu;

                    return (
                      <tr
                        key={item.id || idx}
                        className="hover:bg-slate-50 transition-colors break-inside-avoid"
                      >
                        <td className="border border-black py-1 px-1 text-center font-mono text-[8.5pt]">
                          {idx + 1}
                        </td>
                        <td className="border border-black py-1 px-2 text-left leading-snug">
                          <div className="font-semibold text-slate-900">
                            {item.productName || item.tenSp}
                          </div>
                          <div className="text-[8pt] text-slate-600 flex items-center gap-1.5 mt-0.5">
                            <span className="font-bold text-indigo-700">{item.poCode}</span>
                            <span>•</span>
                            <span>{item.customerName}</span>
                            {item.productCode && (
                              <>
                                <span>•</span>
                                <span className="font-mono text-slate-500">{item.productCode}</span>
                              </>
                            )}
                            {isDone ? (
                              <span className="inline-block px-1 py-0.2 bg-emerald-100 text-emerald-800 rounded text-[7.5pt] font-bold">
                                ✓ Xong
                              </span>
                            ) : item.slDat > 0 ? (
                              <span className="inline-block px-1 py-0.2 bg-amber-100 text-amber-800 rounded text-[7.5pt] font-bold">
                                {completionPct}%
                              </span>
                            ) : null}
                          </div>
                          {item.technicalRequirements && (
                            <div className="text-[7.5pt] text-slate-500 italic mt-0.5">
                              Y/C: {item.technicalRequirements}
                            </div>
                          )}
                        </td>
                        <td className="border border-black py-1 px-1 text-right font-mono">
                          {formatNumber(item.slCap)}
                        </td>
                        <td className="border border-black py-1 px-1 text-right font-mono">
                          {formatNumber(item.slBu)}
                        </td>
                        <td className="border border-black py-1 px-1 text-right font-mono font-semibold">
                          {formatNumber(item.slDuKien)}
                        </td>
                        <td className="border border-black py-1 px-1 text-right font-mono font-bold text-emerald-800">
                          {item.slDat > 0 ? formatNumber(item.slDat) : '—'}
                        </td>
                        <td className={`border border-black py-1 px-1 text-right font-mono ${
                          item.slHu > 0 ? 'text-rose-700 font-bold' : 'text-slate-400'
                        }`}>
                          {item.slHu > 0 ? formatNumber(item.slHu) : '0'}
                        </td>
                        <td className={`border border-black py-1 px-1 text-right font-mono font-bold ${
                          isExcessScrap ? 'text-rose-700 bg-rose-50' : 'text-slate-800'
                        }`}>
                          {formatNumber(item.haoHut)}
                        </td>
                        <td className="border border-black py-1 px-1.5 text-left text-[8pt] text-slate-700 truncate">
                          {item.nguoiThucHien || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })
          )}

          {/* Grand Total Row */}
          {groupKeys.length > 0 && (
            <tr className="bg-emerald-100 font-bold text-slate-900 border-t-2 border-black break-inside-avoid print:bg-slate-200">
              <td colSpan={2} className="border border-black py-1.5 px-2 text-right uppercase">
                TỔNG CỘNG ({items.length} hạng mục):
              </td>
              <td className="border border-black py-1.5 px-1 text-right font-mono">
                {formatNumber(summary?.totalCap)}
              </td>
              <td className="border border-black py-1.5 px-1 text-right font-mono">
                {formatNumber(summary?.totalBu)}
              </td>
              <td className="border border-black py-1.5 px-1 text-right font-mono">
                {formatNumber(summary?.totalDuKien)}
              </td>
              <td className="border border-black py-1.5 px-1 text-right font-mono text-emerald-900">
                {formatNumber(summary?.totalDat)}
              </td>
              <td className="border border-black py-1.5 px-1 text-right font-mono text-rose-800">
                {formatNumber(summary?.totalHu)}
              </td>
              <td className="border border-black py-1.5 px-1 text-right font-mono text-slate-900">
                {formatNumber(summary?.totalHao)}
              </td>
              <td className="border border-black py-1.5 px-1"></td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ========== SIGNATURE SECTION ========== */}
      <div className="grid grid-cols-4 gap-4 mt-6 text-center text-[9pt] break-inside-avoid">
        <div>
          <div className="font-bold uppercase text-slate-900">NGƯỜI LẬP BIỂU</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-800">Tổ Kế Hoạch SX</div>
        </div>
        <div>
          <div className="font-bold uppercase text-slate-900">QUẢN ĐỐC XƯỞNG</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-800">Khối Sản Xuất</div>
        </div>
        <div>
          <div className="font-bold uppercase text-slate-900">TRƯỞNG PHÒNG KHSX</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, ghi rõ họ tên)</div>
          <div className="font-semibold text-slate-800">Phòng Điều Độ SX</div>
        </div>
        <div>
          <div className="font-bold uppercase text-slate-900">BAN GIÁM ĐỐC</div>
          <div className="text-[8pt] text-slate-500 italic mb-14">(Ký, duyệt)</div>
          <div className="font-semibold text-slate-800">Giám Đốc Nhà Máy</div>
        </div>
      </div>
    </div>
  );
}
